import React, { FC, useMemo } from 'react';
import cn from 'classnames';
import { AviaTraveller, OrderStatus, ChangeTravellerDataTravellerAvailability } from '@websky/graphql';
import PassengersDetails from '../PassengersDetails/PassengersDetails';
import { useIsMobile } from '../../../utils';
import { MobileDialog } from '@shared/ui';
import { useTheme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import { useChangeLink } from '../../hooks';
import { BaseButton, ModalX } from '../../../BaseComponents';
import ModalConfirm from '../../../ModalConfirm';

const errorLocale = `Could not be change the 'LinkedTo' link for traveler`;

interface Props {
	className?: string;
	orderId: string;
	orderStatus: OrderStatus;
	travellers: AviaTraveller[];
	changeTravellersAvailability?: ChangeTravellerDataTravellerAvailability[];
	goToChangePassenger?: (passengerId: string) => void;

	isOpen: boolean;
	onClose: () => void;
}

const PassengersDetailsModal: FC<Props> = ({ isOpen, onClose, orderId, ...props }) => {
	const { t } = useTranslation('OrderCard');
	const { PassengersDetailsModal: css } = useTheme('PassengersDetails');

	const isMobile = useIsMobile();

	const changeLink = useChangeLink(orderId);

	const { confirmRelinking, isLoading, isRelinkingMode, error, clearError, onCloseRelinkMode } = changeLink;

	const title = isRelinkingMode ? t('Select an adult for accompany') : t('Passengers details');

	const errorKey = useMemo(() => {
		if (error?.includes(errorLocale)) {
			return errorLocale;
		}

		return error;
	}, [error]);

	const handleCloseModal = () => {
		if (isLoading) {
			return;
		}

		onClose();

		setTimeout(() => {
			onCloseRelinkMode();
		}, 100);
	};

	const confirmButton = (
		<BaseButton
			className={css.confirm__button}
			variant="smallThird"
			isLoading={isLoading}
			onClick={confirmRelinking}
		>
			{t('Confirm')}
		</BaseButton>
	);

	const mobileDialogFooter = <div className={css.mobileDialog__footer}>{confirmButton}</div>;

	const modalXFooter = (
		<div className={css.modalX__footer}>
			<BaseButton variant="smallSecondary" disabled={isLoading} onClick={onCloseRelinkMode}>
				{t('Cancel')}
			</BaseButton>

			{confirmButton}
		</div>
	);

	return (
		<>
			{isMobile ? (
				<MobileDialog
					classes={{
						root: cn(css.mobileDialog__root, { [css.mobileDialog__relink]: isRelinkingMode }),
						paper: css.mobileDialog__paper,
						mobileDialog__header: css.mobileDialog__header,
						mobileDialog__title: css.mobileDialog__title
					}}
					title={title}
					isOpen={isOpen}
					onClose={handleCloseModal}
					onGoBackward={isRelinkingMode ? onCloseRelinkMode : undefined}
					footer={isRelinkingMode ? mobileDialogFooter : undefined}
				>
					<div className={css.mobileDialog__content}>
						<PassengersDetails {...props} {...changeLink} />
					</div>
				</MobileDialog>
			) : (
				<ModalX
					classes={{ paper: css.modalX__paper, top_wrapper: css.modalX__top_wrapper }}
					title={<div className={css.modalX__title}>{title}</div>}
					size={'large'}
					isOpen={isOpen}
					onClose={handleCloseModal}
					keepMounted={false}
					footer={isRelinkingMode ? modalXFooter : undefined}
				>
					<PassengersDetails {...props} {...changeLink} />
				</ModalX>
			)}

			<ModalConfirm
				isOpen={!!error}
				title={t('Oops...')}
				onClose={clearError}
				confirmBtn={t('OK')}
				onConfirm={clearError}
			>
				<div className={css.modalConfirm__content}>
					<span>
						{t(
							'An error occurred while executing the request. Please try again later or contact a customer support service.'
						)}
					</span>

					<span className={css.modalConfirm__error}>{t(errorKey)}</span>
				</div>
			</ModalConfirm>
		</>
	);
};

export default PassengersDetailsModal;
